import { Helmet } from "react-helmet-async";

const SITE_URL = "https://leadiqpro.io";
const DEFAULT_OG_IMAGE = "https://leadiqpro.io/logo-512.png";

/**
 * Per-page SEO head. Renders title / meta description / OG tags / Twitter card /
 * canonical / optional JSON-LD. Overrides the defaults in `public/index.html`.
 *
 * Usage:
 *   <SeoHead
 *     title="Pricing — LeadIQ Pro"
 *     description="…"
 *     path="/pricing"
 *     jsonLd={pricingSchema}
 *   />
 *
 * `jsonLd` may be a single object or an array of schema objects.
 */
export default function SeoHead({
  title,
  description,
  path = "/",
  image,
  type = "website",
  jsonLd,
  noIndex = false,
}) {
  const url = `${SITE_URL}${path.startsWith("/") ? path : `/${path}`}`;
  const ogImage = image || DEFAULT_OG_IMAGE;
  const schemas = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

  return (
    <Helmet>
      {title && <title>{title}</title>}
      {description && <meta name="description" content={description} />}
      <link rel="canonical" href={url} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      {title && <meta property="og:title" content={title} />}
      {description && <meta property="og:description" content={description} />}
      <meta property="og:image" content={ogImage} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      {title && <meta name="twitter:title" content={title} />}
      {description && (
        <meta name="twitter:description" content={description} />
      )}
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD */}
      {schemas.map((schema, i) => (
        <script type="application/ld+json" key={`jsonld-${i}`}>
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}
